/**
 * TanStack Query hook + error mapping for public company self-registration
 * (`POST /api/v1/registration/company`).
 */
import { useMutation } from '@tanstack/react-query';
import type { UseMutationResult } from '@tanstack/react-query';

import type { NormalizedApiError } from '@/shared/api';
import i18n from '@/shared/i18n/i18n';

import { registerCompany } from './registrationApi';
import type { CompanyRegistrationRequest, CompanyRegistrationResult } from './registrationApi';

/** Provision a new company (tenant + owner user) via the BFF; the owner then verifies their email. */
export function useRegisterCompany(): UseMutationResult<
  CompanyRegistrationResult,
  NormalizedApiError,
  CompanyRegistrationRequest
> {
  return useMutation<CompanyRegistrationResult, NormalizedApiError, CompanyRegistrationRequest>({
    mutationFn: registerCompany,
  });
}

/**
 * Map a registration failure to a user-facing message, mirroring the documented
 * `POST /api/v1/registration/company` responses (`openapi.yml`):
 *  - `503` / `IDENTITY_UNAVAILABLE` — identity provisioning temporarily unavailable;
 *  - `400` — request validation failed.
 * Falls back to the normalised server message for anything else.
 *
 * Resolved via the i18next singleton (not `useTranslation`, since this is a plain
 * function, not a component) — see `catalog/lib/readiness.ts` for the same pattern.
 */
export function registrationErrorMessage(error: NormalizedApiError): string {
  if (error.status === 503 || error.code === 'IDENTITY_UNAVAILABLE') {
    return i18n.t('app:signUp.errors.identityUnavailable');
  }
  if (error.status === 400) {
    return i18n.t('app:signUp.errors.validationFailed');
  }
  return error.message || i18n.t('app:signUp.errors.generic');
}
